import { useCallback, useEffect, useRef } from "react";

/* ============================================================================
   useDialogo — el comportamiento delicado de un diálogo, en un solo sitio.

   Se extrae de UI/Modal/Modal.js SIN cambiarlo, para que lo compartan ese
   modal (confirmaciones y formularios de 1-4 campos) y HCModal (los
   formularios largos de Historia Clínica). Duplicar una trampa de foco es
   pedir que las dos copias se separen.

   Cubre:
   · Pila de diálogos: uno puede abrir otro encima y solo el de arriba
     reacciona a Escape y atrapa el Tab.
   · Esc y clic en el backdrop cierran, salvo si hay cambios sin guardar:
     entonces se pide confirmación.
   · El foco entra al abrir y vuelve al elemento que lo abrió al cerrar.

   Devuelve { cajaRef, intentarCerrar }: `cajaRef` va en el contenedor del
   diálogo, `intentarCerrar` en el botón de cerrar y en el backdrop.
   ========================================================================== */

const pila = [];

export const FOCUSABLES = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled]):not([type=hidden])",
    "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
].join(",");

export default function useDialogo({
    abierto,
    hayCambiosSinGuardar = false,
    mensajeDescartar = "Hay cambios sin guardar. ¿Descartarlos?",
    onCerrar,
}) {
    const cajaRef = useRef(null);
    const origenFocoRef = useRef(null);

    const intentarCerrar = useCallback(() => {
        if (hayCambiosSinGuardar) {
            // eslint-disable-next-line no-alert
            if (!window.confirm(mensajeDescartar)) return;
        }
        onCerrar && onCerrar();
    }, [hayCambiosSinGuardar, mensajeDescartar, onCerrar]);

    // Foco: se recuerda de dónde se venía, se lleva el foco dentro y al cerrar
    // se devuelve exactamente al elemento que lo abrió.
    useEffect(() => {
        if (!abierto) return undefined;
        origenFocoRef.current = document.activeElement;
        const caja = cajaRef.current;
        const primerFocusable = caja && caja.querySelector(FOCUSABLES);
        (primerFocusable || caja)?.focus();
        return () => {
            const origen = origenFocoRef.current;
            if (origen && typeof origen.focus === "function") origen.focus();
        };
    }, [abierto]);

    // Esc cierra; Tab queda atrapado dentro del diálogo de arriba de la pila.
    useEffect(() => {
        if (!abierto) return undefined;
        const yo = {};
        pila.push(yo);
        const soyElDeArriba = () => pila[pila.length - 1] === yo;
        const alPulsar = (e) => {
            if (!soyElDeArriba()) return;
            if (e.key === "Escape") {
                e.stopPropagation();
                intentarCerrar();
                return;
            }
            if (e.key !== "Tab") return;
            const caja = cajaRef.current;
            if (!caja) return;
            const focusables = [...caja.querySelectorAll(FOCUSABLES)];
            if (focusables.length === 0) return;
            const primero = focusables[0];
            const ultimo = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === primero) {
                e.preventDefault();
                ultimo.focus();
            } else if (!e.shiftKey && document.activeElement === ultimo) {
                e.preventDefault();
                primero.focus();
            }
        };
        document.addEventListener("keydown", alPulsar, true);
        return () => {
            document.removeEventListener("keydown", alPulsar, true);
            const i = pila.indexOf(yo);
            if (i !== -1) pila.splice(i, 1);
        };
    }, [abierto, intentarCerrar]);

    return { cajaRef, intentarCerrar };
}
